import React from 'react';
import {
  Box,
  LinearProgress,
  Rating,
  Chip,
  CircularProgress,
  Typography,
  Tooltip,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { acme } from '../../theme/acmeTheme';

function IndicatorRefreshIconButton({ onRefresh, loading, sx = {}, progressSize = 16 }) {
  if (!onRefresh) return null;
  return (
    <IconButton
      size="small"
      onClick={onRefresh}
      disabled={loading}
      sx={sx}
    >
      {loading ? <CircularProgress size={progressSize} /> : <RefreshIcon fontSize="small" />}
    </IconButton>
  );
}

// Функция для определения цвета спам-рейтинга (инвертированная логика: меньше = лучше)
const getSpamScoreColor = (score) => {
  if (score <= 20) return 'success';    // Отличный результат (зеленый)
  if (score <= 40) return 'warning';    // Средний результат (оранжевый)  
  return 'error';                       // Плохой результат (красный)
};

// Функция для получения цвета фона спам-рейтинга
const getSpamScoreBackgroundColor = (score) => {
  if (score <= 20) return '#e8f5e8';    // Светло-зеленый фон
  if (score <= 40) return '#fff3e0';    // Светло-оранжевый фон
  return '#ffebee';                     // Светло-красный фон
};

// Функция для получения текста статуса спам-рейтинга
const getSpamScoreStatusText = (score) => {
  if (score <= 20) return 'Excellent';
  if (score <= 40) return 'Good';
  if (score <= 60) return 'Average';
  return 'Poor';
};

// Функция для определения цвета вовлеченности
const getEngagementColor = (rate) => {
  if (rate >= 70) return 'success';
  if (rate >= 40) return 'warning';
  return 'error';
};

// Функция для определения цвета круговых индикаторов
const getCircularColor = (value) => {
  if (value >= 80) return acme.colors.brand;
  if (value >= 60) return acme.colors.brand;
  if (value >= 40) return '#f44336'; // Красный
  return '#9e9e9e'; // Серый
};

// Компонент спам-рейтинга с улучшенной визуализацией (меньше = лучше)
export const SpamScoreIndicator = ({ score, onRefresh, loading = false }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">
            Spam Score: {Math.round(score)}% ({getSpamScoreStatusText(score)})
          </Typography>
          <Typography variant="caption">
            Probability of landing in spam folder. Lower is better!
          </Typography>
          <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
            <li>Email delivery rates</li>
            <li>Open rates and engagement</li>
            <li>Unsubscribe rates</li>
            <li>Domain DNS configuration</li>
            <li>Email address age</li>
          </ul>
          <Typography variant="caption" color="textSecondary">
            {score <= 20 && "🟢 Excellent! Very low spam risk."}
            {score > 20 && score <= 40 && "🟡 Good rating. Low spam risk."}
            {score > 40 && score <= 60 && "🟠 Average. Consider optimizing."}
            {score > 60 && "🔴 High spam risk. Urgent optimization required."}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box display="flex" alignItems="center" width="100%">
        {/* Статус чип */}
        <Chip 
          label={getSpamScoreStatusText(score)}
          size="small"
          sx={{
            backgroundColor: getSpamScoreBackgroundColor(score),
            color: score <= 20 ? acme.colors.brand : score <= 40 ? '#ed6c02' : acme.colors.destructive,
            fontWeight: 'bold',
            fontSize: '0.75rem',
            minWidth: '65px',
            mr: 1
          }}
        />
        
        {/* Прогресс-бар */}
        <Box width={60} mr={1}>
          <LinearProgress 
            variant="determinate" 
            value={score}
            color={getSpamScoreColor(score)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: '#f5f5f5',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
              }
            }}
          />
        </Box>
        
        {/* Процент */}
        <Typography 
          variant="body2" 
          fontWeight="medium" 
          minWidth="35px"
          sx={{
            color: score <= 20 ? acme.colors.brand : score <= 40 ? '#ed6c02' : acme.colors.destructive
          }}
        >
          {Math.round(score)}%
        </Typography>
        
        {/* Кнопка обновления */}
        <IndicatorRefreshIconButton onRefresh={onRefresh} loading={loading} sx={{ ml: 0.5 }} />
      </Box>
    </Tooltip>
  </Box>
);

// Компонент репутации со звездочками
export const ReputationIndicator = ({ reputation, onRefresh, loading = false }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">Reputation: {reputation}/100</Typography>
          <Typography variant="caption">
            Email address quality assessment. Higher is better! Based on:
          </Typography>
          <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
            <li>Email open rates (primary factor)</li>
            <li>Click-through rates</li>
            <li>Low unsubscribe rates</li>
            <li>Sending activity and consistency</li>
            <li>Address verification and age</li>
          </ul>
          <Typography variant="caption" color="textSecondary">
            {reputation >= 80 && "🌟 Excellent reputation! Outstanding performance."}
            {reputation >= 60 && reputation < 80 && "⭐ Good reputation. Strong performance."}
            {reputation >= 40 && reputation < 60 && "📊 Average reputation. Room for improvement."}
            {reputation < 40 && "📉 Low reputation. Focus on engagement."}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box display="flex" alignItems="center">
        <Rating 
          value={reputation / 20} 
          precision={0.1} 
          readOnly 
          size="small"
          sx={{
            '& .MuiRating-iconFilled': {
              color: reputation >= 80 ? acme.colors.brand : reputation >= 60 ? '#ff9800' : acme.colors.destructive
            }
          }}
        />
        <Typography variant="caption" sx={{ ml: 0.5, minWidth: '30px' }}>
          {Math.round(reputation / 20 * 10) / 10}
        </Typography>
        <IndicatorRefreshIconButton onRefresh={onRefresh} loading={loading} sx={{ ml: 0.5 }} />
      </Box>
    </Tooltip>
  </Box>
);

// Компонент вовлеченности с цветными чипсами
export const EngagementIndicator = ({ engagementRate, onRefresh, loading = false }) => (
  <Box display="flex" alignItems="center" gap={1}>
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">Engagement: {Math.round(engagementRate)}%</Typography>
          <Typography variant="caption">
            Recipient activity level. Higher is better! Based on:
          </Typography>
          <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
            <li>Email opens (primary - up to 70 points)</li>
            <li>Link clicks (secondary - up to 25 points)</li>
            <li>Low unsubscribe rates (penalty reducer)</li>
            <li>Consistent sending activity (bonus)</li>
          </ul>
          <Typography variant="caption" color="textSecondary">
            {engagementRate >= 70 && "🚀 High engagement! Excellent audience response."}
            {engagementRate >= 40 && engagementRate < 70 && "📈 Medium engagement. Good performance."}
            {engagementRate < 40 && "📊 Low engagement. Consider content optimization."}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box display="flex" alignItems="center">
        <Chip 
          label={`${Math.round(engagementRate)}%`}
          color={getEngagementColor(engagementRate)}
          size="small"
          variant="filled"
          sx={{
            fontWeight: 'medium',
            minWidth: '55px'
          }}
        />
        <IndicatorRefreshIconButton onRefresh={onRefresh} loading={loading} sx={{ ml: 0.5 }} />
      </Box>
    </Tooltip>
  </Box>
);

// Компонент круговых индикаторов для доменов
export const CircularIndicator = ({ value, label, size = 60, onRefresh, loading = false }) => (
  <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
    <Tooltip 
      title={
        <Box>
          <Typography variant="body2" fontWeight="bold">{label}: {value}%</Typography>
          <Typography variant="caption">
            {label === 'Trust' && (
              <>
                Domain trust rating. Based on:
                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                  <li>DNS record configuration (SPF, DKIM, DMARC)</li>
                  <li>Number of verified emails</li>
                  <li>Domain age</li>
                </ul>
              </>
            )}
            {label === 'Security' && (
              <>
                Domain security level. Includes:
                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                  <li>Required DNS records (SPF, DKIM, DMARC)</li>
                  <li>Additional measures (BIMI)</li>
                  <li>Successful delivery statistics</li>
                </ul>
              </>
            )}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {value >= 80 && "Excellent level!"}
            {value >= 60 && value < 80 && "Good level."}
            {value >= 40 && value < 60 && "Average level."}
            {value < 40 && "Improvement needed."}
          </Typography>
        </Box>
      }
      arrow
      placement="top"
    >
      <Box position="relative" display="inline-flex" alignItems="center">
        <CircularProgress
          variant="determinate"
          value={value}
          size={size}
          thickness={4}
          sx={{
            color: getCircularColor(value),
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        <Box
          top={0}
          left={0}
          bottom={0}
          right={0}
          position="absolute"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Typography 
            variant="caption" 
            component="div" 
            color="textPrimary"
            fontWeight="bold"
            fontSize={size > 50 ? '0.875rem' : '0.75rem'}
          >
            {Math.round(value)}%
          </Typography>
        </Box>
        <IndicatorRefreshIconButton
          onRefresh={onRefresh}
          loading={loading}
          progressSize={12}
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            backgroundColor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              backgroundColor: 'background.paper'
            }
          }}
        />
      </Box>
    </Tooltip>
    <Typography 
      variant="caption" 
      color="textSecondary" 
      fontWeight="medium"
      textAlign="center"
    >
      {label}
    </Typography>
  </Box>
);
